import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { paymentAPI } from '../services/api';
import {
  markPendingStayBooking,
  clearPendingStayBooking,
  releasePendingStayBooking,
  releaseStalePendingStayBooking
} from '../utils/pendingStayBooking';
import {
  saveStayBookingDraft,
  loadStayBookingDraft,
  clearStayBookingDraft
} from '../utils/stayBookingDraft';
import CashfreePayment from '../components/CashfreePayment';
import Footer from '../components/Footer';
import { normalizeRoomBookingOption } from '../utils/roomBookingOptions';
import {
  computeStayTotalsFromRooms,
  getStayNightsFromDates
} from '../utils/bookingPricing';
import { dateToYmd } from '../utils/dateYmd';
import staysApi from '../services/staysApi';
import { getStayCancellationPolicyText } from '../utils/cancellationPolicy';

function normalizeMobileDisplay(raw) {
  if (raw == null || raw === '') return '';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 0) return '';
  if (d.length === 10) return d;
  if (d.startsWith('91') && d.length >= 12) return d.slice(-10);
  if (d.length > 10) return d.slice(-10);
  return d;
}

function deriveTravellerFromSources(bookingUser, authUser) {
  const bu = bookingUser || {};
  const au = authUser || {};
  let firstName = bu.firstName || au.firstName || '';
  let lastName = bu.lastName || au.lastName || '';
  if (!firstName && !lastName) {
    const full = (bu.name || au.name || '').trim();
    if (full) {
      const parts = full.split(/\s+/);
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }
  }
  return {
    firstName,
    lastName,
    email: bu.email || au.email || '',
    mobile: normalizeMobileDisplay(bu.mobile || au.mobile || '')
  };
}

const BookingReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [cancellationPolicyText, setCancellationPolicyText] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const bookingIdRef = useRef(null);
  const paymentCompletedRef = useRef(false);
  const [traveller, setTraveller] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: ''
  });

  // Get property ID from URL params or location state
  const propertyId = id || location.state?.property?.id;

  // Restore booking from navigation state or session draft (survives PG redirect / browser back)
  useEffect(() => {
    const fromState = location.state?.bookingData;
    const fromDraft = loadStayBookingDraft(propertyId);
    const source = fromState || fromDraft;

    const sanitizeDates = (data) => {
        if (data.dates) {
          const dates = data.dates;
          // Preserve Date objects before converting to formatted strings
          if (dates.checkIn instanceof Date) {
            // Preserve as checkInDate before converting to string
            if (!dates.checkInDate) {
              dates.checkInDate = dates.checkIn;
            }
            dates.checkIn = dates.checkIn.toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            });
          }
          if (dates.checkOut instanceof Date) {
            // Preserve as checkOutDate before converting to string
            if (!dates.checkOutDate) {
              dates.checkOutDate = dates.checkOut;
            }
            dates.checkOut = dates.checkOut.toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short'
            });
          }
          // Ensure checkInDate and checkOutDate are Date objects
          if (dates.checkInDate && !(dates.checkInDate instanceof Date)) {
            // If it's already a string (ISO or other format), try to parse it
            const parsed = new Date(dates.checkInDate);
            if (!isNaN(parsed.getTime())) {
              dates.checkInDate = parsed;
            }
          }
          if (dates.checkOutDate && !(dates.checkOutDate instanceof Date)) {
            // If it's already a string (ISO or other format), try to parse it
            const parsed = new Date(dates.checkOutDate);
            if (!isNaN(parsed.getTime())) {
              dates.checkOutDate = parsed;
            }
          }
        }
        return data;
    };

    const normalizeRooms = (sanitizedData) => {
      if (sanitizedData.selectedRoom && !sanitizedData.selectedRooms) {
        sanitizedData.selectedRooms = [
          {
            id: sanitizedData.selectedRoom.id,
            name: sanitizedData.selectedRoom.name,
            quantity: sanitizedData.selectedRoom.quantity || 1,
            price: sanitizedData.selectedRoom.price || 0,
            taxes: sanitizedData.selectedRoom.taxes || 0,
            image: sanitizedData.selectedRoom.image,
            bedType: sanitizedData.selectedRoom.bedType,
            sleeps: sanitizedData.selectedRoom.sleeps,
            selectedOption: normalizeRoomBookingOption(sanitizedData.selectedRoom.selectedOption)
          }
        ];
      }
      return sanitizedData;
    };

    if (source) {
      const sanitizedData = normalizeRooms(sanitizeDates({ ...source }));
      setBookingData(sanitizedData);
      if (fromState) {
        saveStayBookingDraft(propertyId, sanitizedData);
      }
    } else {
      setBookingData({
        room: {
          name: "Standard Twin - Double",
          image: "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop",
          bedType: "1 Queen Bed",
          sleeps: 2,
          guests: 4
        },
        property: {
          id: propertyId || "1",
          name: "Timbertales Luxury Hotels & Resort",
          location: "Andheri East, 3.1 Km from Chatrapati Shivaji International Airport",
          image: "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop"
        },
        dates: {
          checkIn: "Wed, 30 Jun",
          checkOut: "Thu, 31 Jun",
          checkInTime: "2:00 PM",
          checkOutTime: "11:00 AM",
          duration: "1 Night"
        },
        pricing: {
          roomPrice: 4400,
          taxes: 671.19,
          discount: 1434,
          total: 2966
        },
        user: {
          firstName: "Aromal",
          lastName: "Sula",
          email: "Aromalsula@gmail.com",
          mobile: "6282937901"
        }
      });
    }
  }, [location.state, propertyId]);

  // Browser back from Cashfree restores this page without re-opening checkout overlay
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setPaymentSessionId(null);
        setIsProcessingPayment(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (location.state?.paymentError) {
      setPaymentError(location.state.paymentError);
      setPaymentSessionId(null);
      setIsProcessingPayment(false);
    }
  }, [location.state?.paymentError]);

  useEffect(() => {
    if (!propertyId && !bookingData?.property) return undefined;

    const fromBooking = getStayCancellationPolicyText(bookingData?.property);
    const hasCustomPolicy =
      bookingData?.property?.cancellationPolicy?.trim() ||
      (bookingData?.property?.houseRules || []).some((rule) =>
        /cancellation/i.test(rule?.ruleName || '')
      );

    if (hasCustomPolicy) {
      setCancellationPolicyText(fromBooking);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await staysApi.getStayById(propertyId);
        if (cancelled) return;
        setCancellationPolicyText(getStayCancellationPolicyText(response?.stay));
      } catch {
        if (!cancelled) setCancellationPolicyText(fromBooking);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [propertyId, bookingData?.property]);

  const bookingSessionKey = useMemo(() => {
    if (!bookingData) return '';
    const rooms = (bookingData.selectedRooms || [])
      .map((r) => `${r.id}:${r.quantity || 1}`)
      .join('|');
    return [propertyId, bookingData.dates?.checkIn, bookingData.dates?.checkOut, rooms].join('::');
  }, [bookingData, propertyId]);

  const lastTravellerInitKey = useRef('');

  useEffect(() => {
    if (!bookingData || !bookingSessionKey) return;
    const isNewSession = lastTravellerInitKey.current !== bookingSessionKey;
    if (isNewSession) {
      lastTravellerInitKey.current = bookingSessionKey;
      setTraveller(deriveTravellerFromSources(bookingData.user, user));
      return;
    }
    if (!user) return;
    setTraveller((prev) => {
      const allEmpty = !prev.firstName && !prev.lastName && !prev.email && !prev.mobile;
      if (!allEmpty) return prev;
      return deriveTravellerFromSources(bookingData.user, user);
    });
  }, [bookingData, bookingSessionKey, user]);

  useEffect(() => {
    bookingIdRef.current = bookingId;
  }, [bookingId]);

  const releaseActiveCheckout = useCallback(async () => {
    if (paymentCompletedRef.current) return;
    const id = bookingIdRef.current;
    if (id) {
      await releasePendingStayBooking(id);
    } else {
      await releaseStalePendingStayBooking();
    }
  }, []);

  const handleBack = async () => {
    await releaseActiveCheckout();
    // Check if we came from RoomSelection and navigate accordingly
    if (location.state?.fromRoomSelection) {
      const propertyId = id || location.state?.property?.id;
      navigate(`/property/${propertyId}/rooms`);
    } else {
      navigate(-1);
    }
  };

  const handlePayNow = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to continue with payment');
      navigate('/');
      return;
    }

    if (!bookingData) {
      alert('Booking data is missing. Please try again.');
      return;
    }

    const guestFirst = traveller.firstName.trim();
    const guestLast = traveller.lastName.trim();
    const guestName = `${guestFirst} ${guestLast}`.trim();
    if (guestName.length < 2) {
      alert('Please enter your first and last name.');
      return;
    }
    const guestEmail = traveller.email.trim();
    if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      alert('Please enter a valid email address.');
      return;
    }
    const mobileDigits = traveller.mobile.replace(/\D/g, '').slice(-10);
    if (mobileDigits.length !== 10) {
      alert('Please enter a valid 10-digit Indian mobile number.');
      return;
    }
    const guestMobileE164 = `+91${mobileDigits}`;

    try {
      setIsProcessingPayment(true);
      setPaymentError(null);

      // Prepare booking payload for payment initiation
      // Get dates - prefer Date objects, fallback to parsing formatted strings or current date
      let checkInDate, checkOutDate;
      
      if (bookingData.dates?.checkInDate) {
        checkInDate = bookingData.dates.checkInDate instanceof Date 
          ? bookingData.dates.checkInDate 
          : new Date(bookingData.dates.checkInDate);
      } else if (bookingData.dates?.checkIn) {
        // Try to parse the formatted string (e.g., "Wed, 30 Jun")
        // This is tricky, so we'll use a fallback approach
        checkInDate = new Date(); // Fallback - should not happen in normal flow
      } else {
        checkInDate = new Date();
      }
      
      if (bookingData.dates?.checkOutDate) {
        checkOutDate = bookingData.dates.checkOutDate instanceof Date 
          ? bookingData.dates.checkOutDate 
          : new Date(bookingData.dates.checkOutDate);
      } else if (bookingData.dates?.checkOut) {
        checkOutDate = new Date(); // Fallback - should not happen in normal flow
      } else {
        checkOutDate = new Date();
      }
      
      // Validate dates
      if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
        throw new Error('Invalid check-in or check-out dates');
      }
      
      // Normalize room data - handle both selectedRoom (singular) and selectedRooms (plural)
      let roomsToProcess = [];
      
      if (bookingData.selectedRooms && bookingData.selectedRooms.length > 0) {
        roomsToProcess = bookingData.selectedRooms;
      } else if (bookingData.selectedRoom) {
        // Convert single room to array format
        roomsToProcess = [{
          id: bookingData.selectedRoom.id,
          quantity: bookingData.selectedRoom.quantity || 1,
          selectedOption: bookingData.selectedRoom.selectedOption || 'roomOnly'
        }];
      } else if (bookingData.room) {
        // Fallback for old format
        roomsToProcess = [{
          id: bookingData.room.id || 'room_1',
          quantity: 1,
          selectedOption: 'roomOnly'
        }];
      }
      
      if (roomsToProcess.length === 0) {
        console.error('Booking data:', bookingData);
        throw new Error('No rooms selected. Please go back and select a room.');
      }
      
      const checkInYmd = dateToYmd(checkInDate);
      const checkOutYmd = dateToYmd(checkOutDate);
      if (!checkInYmd || !checkOutYmd || checkInYmd >= checkOutYmd) {
        throw new Error('Invalid check-in or check-out dates');
      }

      const bookingPayload = {
        stayId: bookingData.property?.id || propertyId,
        checkIn: checkInYmd,
        checkOut: checkOutYmd,
        guestInfo: {
          name: guestName,
          email: guestEmail,
          mobile: guestMobileE164
        },
        occupancy: {
          adults: Math.max(1, Number(bookingData.guestData?.adults) || 2),
          children: Number(bookingData.guestData?.children) || 0
        },
        rooms: roomsToProcess.map(room => ({
          roomId: room.id,
          quantity: room.quantity || 1,
          option: normalizeRoomBookingOption(room.selectedOption)
        }))
      };
      
      console.log('Payment payload:', bookingPayload);

      // Initiate payment (this creates booking and payment session)
      const paymentResponse = await paymentAPI.initiatePayment(bookingPayload);

      if (paymentResponse.success && paymentResponse.paymentSessionId) {
        saveStayBookingDraft(propertyId, bookingData);
        setPaymentSessionId(paymentResponse.paymentSessionId);
        setOrderId(paymentResponse.orderId);
        setBookingId(paymentResponse.bookingId);
        bookingIdRef.current = paymentResponse.bookingId;
        markPendingStayBooking(
          paymentResponse.bookingId,
          paymentResponse.orderId,
          bookingData.property?.id || propertyId
        );
      } else {
        throw new Error('Failed to initialize payment session');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      setPaymentError(error.message || 'Failed to initiate payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = (result) => {
    paymentCompletedRef.current = true;
    clearPendingStayBooking();
    clearStayBookingDraft(propertyId);
    setIsProcessingPayment(false);
    const mobileDigits = traveller.mobile.replace(/\D/g, '').slice(-10);
    const mobileForState = mobileDigits.length === 10 ? `+91${mobileDigits}` : traveller.mobile.trim();
    const guestUser = {
      ...(bookingData.user || {}),
      firstName: traveller.firstName.trim(),
      lastName: traveller.lastName.trim(),
      email: traveller.email.trim(),
      mobile: mobileForState,
      name: `${traveller.firstName} ${traveller.lastName}`.trim()
    };
    // Navigate to booking confirmation with order ID
    navigate('/booking-confirmation', {
      state: {
        bookingData: {
          ...bookingData,
          user: guestUser,
          bookingId: bookingId,
          orderId: orderId,
          paymentSuccess: true
        },
        fromBookingReview: true,
        propertyId: propertyId,
        orderId: orderId
      }
    });
  };

  const handlePaymentFailure = async (error) => {
    console.error('Payment failed:', error);
    setPaymentError(error.message || 'Payment failed. Please try again.');
    setIsProcessingPayment(false);
    setPaymentSessionId(null);
    // Keep pending booking so user can tap Pay now again without losing inventory
  };

  const handlePaymentClose = async () => {
    setPaymentSessionId(null);
    setIsProcessingPayment(false);
    setPaymentError(null);
    await releaseActiveCheckout();
  };

  // Helper function to get total rooms count
  const getTotalRoomsCount = () => {
    if (bookingData?.selectedRooms) {
      return bookingData.selectedRooms.reduce((total, room) => total + (room.quantity || 1), 0);
    }
    return 1;
  };

  // Helper function to get total guests count
  const getTotalGuestsCount = () => {
    // Use actual guest data if available, otherwise fall back to room sleeps
    if (bookingData?.guestData) {
      return bookingData.guestData.adults + bookingData.guestData.children;
    }
    if (bookingData?.selectedRooms) {
      return bookingData.selectedRooms.reduce((total, room) => total + (room.sleeps || 1), 0);
    }
    return bookingData?.room?.guests || 1;
  };

  const stayTotals = useMemo(() => {
    if (!bookingData) {
      return { nights: 1, roomSubtotal: 0, taxes: 0, total: 0 };
    }

    const nights = getStayNightsFromDates(bookingData.dates);
    let rooms = bookingData.selectedRooms;
    if (!rooms?.length && bookingData.selectedRoom) {
      rooms = [
        {
          ...bookingData.selectedRoom,
          quantity: bookingData.selectedRoom.quantity || 1
        }
      ];
    }

    if (rooms?.length) {
      const computed = computeStayTotalsFromRooms(rooms, nights);
      if (computed.total > 0) return computed;
      const fallbackTotal = Number(bookingData.pricing?.total) || 0;
      if (fallbackTotal > 0) {
        return {
          ...computed,
          roomSubtotal: Number(bookingData.pricing?.roomPrice) || computed.roomSubtotal,
          taxes: Number(bookingData.pricing?.taxes) || computed.taxes,
          total: fallbackTotal
        };
      }
      return computed;
    }

    const roomSubtotal = bookingData.pricing?.roomPrice || 0;
    const taxes = bookingData.pricing?.taxes || 0;
    return {
      nights,
      roomSubtotal,
      taxes,
      total: bookingData.pricing?.total ?? roomSubtotal
    };
  }, [bookingData]);

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] md:py-[60px]">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 bg-black text-white p-4 text-xs rounded max-w-md max-h-96 overflow-auto">
          <div className="font-bold mb-2">BookingReview Debug:</div>
          <div>Has location.state: {location.state ? 'Yes' : 'No'}</div>
          <div>Has bookingData: {location.state?.bookingData ? 'Yes' : 'No'}</div>
          <div>Current bookingData: {bookingData ? 'Set' : 'Null'}</div>
          <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
          <div>User: {user?.name || 'Not logged in'}</div>
          {location.state && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div>State keys: {Object.keys(location.state).join(', ')}</div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="p-[16px] max-w-7xl mx-auto">
        <div className="flex items-center justify-start gap-[16px]">
          <button
            onClick={handleBack}
            className=""
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_2846_6264)">
                <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2846_6264">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <h1 className="text-[20px] font-medium">Booking Review</h1>
        </div>
      </div>

      {/* Two Column Layout - Desktop */}
      <div className="px-[16px] pb-[100px] mt-[20px]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3  gap-[20px]">
          {/* Left Column - Booking Summary */}
          <div className="space-y-[20px]">
            {/* Property Details Card */}
            <div className="bg-white rounded-[16px] overflow-hidden shadow-sm flex p-[14px] gap-[8px]">
              <img
                src={bookingData.property?.image || "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop"}
                alt={bookingData.property?.name || "Property"}
                className="w-[100px] h-[100px] rounded-[12px] object-cover"
              />
              <div className="py-[12px]">
                <h3 className="font-medium text-[18px] text-black leading-[1.1] mb-[8px]">
                  {bookingData.property?.name || "Property Name"}
                </h3>
                <p className="text-[14px] text-[#78736A] leading-[1.1]">
                  {bookingData.property?.location || "Location not specified"}
                </p>
              </div>
            </div>

            {/* Fare Summary Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Fare Summary</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="space-y-[20px]">
                <div className="flex justify-between">
                  <span className="text-[#7E7E7E] text-[16px] leading-none">
                    {getTotalRoomsCount()} Room{getTotalRoomsCount() > 1 ? 's' : ''},{' '}
                    {stayTotals.nights} Night{stayTotals.nights > 1 ? 's' : ''}
                  </span>
                  <span className="font-medium text-[16px] leading-none text-[#636363]">
                    ₹{stayTotals.roomSubtotal.toLocaleString()}
                  </span>
                </div>

                {stayTotals.taxes > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#7E7E7E] text-[16px] leading-none">Included Taxes & Charges</span>
                    <span className="font-medium text-[16px] leading-none text-[#636363]">
                      ₹{stayTotals.taxes.toLocaleString()}
                    </span>
                  </div>
                )}

                {bookingData.pricing?.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#7E7E7E] text-[16px] leading-none">Discounts</span>
                    <span className="text-[#2AA62B] text-[16px] leading-none font-medium">
                      ₹{bookingData.pricing.discount.toLocaleString()}
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-[#7E7E7E] text-[16px] leading-none">Total Payable</span>
                  <span className="font-medium text-[20px] leading-none text-[#000]">
                    ₹{stayTotals.total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Traveller and Travel Details */}
          <div className="space-y-[20px] md:col-span-2">
            {/* Traveller Details Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Traveller Details</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="space-y-[20px]">
                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">First Name</label>
                  <input
                    type="text"
                    name="given-name"
                    autoComplete="given-name"
                    value={traveller.firstName}
                    onChange={(e) => setTraveller((t) => ({ ...t, firstName: e.target.value }))}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Last Name</label>
                  <input
                    type="text"
                    name="family-name"
                    autoComplete="family-name"
                    value={traveller.lastName}
                    onChange={(e) => setTraveller((t) => ({ ...t, lastName: e.target.value }))}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={traveller.email}
                    onChange={(e) => setTraveller((t) => ({ ...t, email: e.target.value }))}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Mobile No</label>
                  <div className="flex space-x-2">
                    <div className="flex items-center p-[16px] border border-[#d9d9d9] rounded-[14px] min-w-[80px]">
                      <span className="mr-[8px]">🇮🇳</span>
                      <span className="text-black">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="tel-national"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit number"
                      value={traveller.mobile}
                      onChange={(e) => {
                        const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setTraveller((t) => ({ ...t, mobile: d }));
                      }}
                      className="flex-1 p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Travel Details Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Travel Details</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="flex items-center mb-[20px] justify-between">
                {/* Check-in */}
                <div className="flex flex-col gap-[6px] items-start">
                  <div className="flex items-center space-x-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <g clipPath="url(#clip0_2846_6297)">
                        <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_6297">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="text-[12px] text-[#999999]">Check in</span>
                  </div>
                  <div>
                    <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.dates?.checkIn || "Check-in date"}</div>
                    <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.dates?.checkInTime || "2:00 PM"}</div>
                  </div>
                </div>

                {/* Duration */}
                <div className="text-center">
                  <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">
                    {bookingData.dates?.duration || '1 Night'}
                  </span>
                </div>

                {/* Check-out */}
                <div className="flex flex-col gap-[6px] items-end">
                  <div className="flex items-center space-x-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <g clipPath="url(#clip0_2846_6297)">
                        <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_6297">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="text-[12px] text-[#999999]">Check out</span>
                  </div>
                  <div className="text-end">
                    <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.dates?.checkOut || "Check-out date"}</div>
                    <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.dates?.checkOutTime || "11:00 AM"}</div>
                  </div>
                </div>
              </div>

              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>

              {/* Room Type */}
              <div className="space-y-4">
                {bookingData.selectedRooms && bookingData.selectedRooms.length > 0 ? (
                  bookingData.selectedRooms.map((room, index) => (
                    <div key={room.id || index} className="flex items-center">
                      <div className="flex items-start space-x-[8px] w-full">
                        <img
                          src={room.image || "https://images.unsplash.com/photo-1566665797739-1674de7a421a"}
                          alt={room.name || "Room"}
                          className="w-[60px] h-[60px] rounded-[9px] object-cover"
                        />
                        <div className='pt-[6px] pb-[12px] flex-1'>
                          <div className="font-medium leading-none mb-[6px] text-[18px]">
                            {room.quantity || 1} x {room.name || "Room"}
                          </div>
                          <div className="flex items-center gap-[8px] mb-[6px]">
                            <div className='flex items-center space-x-[4px]'>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <g clipPath="url(#clip0_2846_5962)">
                                  <path d="M2.91699 5.24992C2.91699 5.55934 3.03991 5.85608 3.2587 6.07488C3.47749 6.29367 3.77424 6.41659 4.08366 6.41659C4.39308 6.41659 4.68982 6.29367 4.90862 6.07488C5.12741 5.85608 5.25033 5.55934 5.25033 5.24992C5.25033 4.9405 5.12741 4.64375 4.90862 4.42496C4.68982 4.20617 4.39308 4.08325 4.08366 4.08325C3.77424 4.08325 3.47749 4.20617 3.2587 4.42496C3.03991 4.64375 2.91699 4.9405 2.91699 5.24992Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12.8337 9.91675V8.16675H1.16699" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M1.16699 4.66675V9.91675" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_2846_5962">
                                    <rect width="14" height="14" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                              <p className='text-[12px] size-max leading-none text-[#bebebe]'>{room.bedType || "Bed type not specified"}</p>
                            </div>
                            <div className='flex size-max items-center space-x-[4px]'>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <g clipPath="url(#clip0_2846_5970)">
                                  <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M9.33301 1.82593C9.83492 1.95444 10.2798 2.24634 10.5975 2.65561C10.9151 3.06488 11.0876 3.56825 11.0876 4.08634C11.0876 4.60444 10.9151 5.10781 10.5975 5.51708C10.2798 5.92635 9.83492 6.21825 9.33301 6.34676" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12.25 12.2499V11.0832C12.247 10.5682 12.0738 10.0687 11.7572 9.66247C11.4407 9.25624 10.9986 8.96614 10.5 8.8374" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_2846_5970">
                                    <rect width="14" height="14" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                              <span className='text-[12px] text-[#bebebe]'>Sleeps {room.sleeps || "2"}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-[4px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <g clipPath="url(#clip0_2846_5880)">
                                <path d="M8.16699 7V7.00583" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.75 12.25H12.25" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M3.5 12.25V2.91667C3.5 2.60725 3.62292 2.3105 3.84171 2.09171C4.0605 1.87292 4.35725 1.75 4.66667 1.75H9.33333C9.64275 1.75 9.9395 1.87292 10.1583 2.09171C10.3771 2.3105 10.5 2.60725 10.5 2.91667V12.25" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_2846_5880">
                                  <rect width="14" height="14" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            <span className='text-[12px] leading-none text-[#78736A]'>
                              {getTotalRoomsCount()} Room {getTotalGuestsCount()} guest
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback to single room display
                  <div className="flex items-center">
                    <div className="flex items-start space-x-[8px]">
                      <img
                        src={bookingData.room?.image || "https://images.unsplash.com/photo-1566665797739-1674de7a421a"}
                        alt={bookingData.room?.name || "Room"}
                        className="w-[60px] h-[60px] rounded-[9px] object-cover"
                      />
                      <div className='pt-[6px] pb-[12px]'>
                        <div className="font-medium leading-none mb-[6px] text-[18px]">
                          {getTotalRoomsCount()} x {bookingData.room?.name || "Room"}
                        </div>
                        <div className="flex items-center space-x-[12px] mb-[6px]">
                          <div className='flex items-center space-x-[4px]'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <g clipPath="url(#clip0_2846_5962)">
                                <path d="M2.91699 5.24992C2.91699 5.55934 3.03991 5.85608 3.2587 6.07488C3.47749 6.29367 3.77424 6.41659 4.08366 6.41659C4.39308 6.41659 4.68982 6.29367 4.90862 6.07488C5.12741 5.85608 5.25033 5.55934 5.25033 5.24992C5.25033 4.9405 5.12741 4.64375 4.90862 4.42496C4.68982 4.20617 4.39308 4.08325 4.08366 4.08325C3.77424 4.08325 3.47749 4.20617 3.2587 4.42496C3.03991 4.64375 2.91699 4.9405 2.91699 5.24992Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12.8337 9.91675V8.16675H1.16699" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.16699 4.66675V9.91675" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_2846_5962">
                                  <rect width="14" height="14" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            <span className='text-[12px] text-[#bebebe]'>{bookingData.room?.bedType || "Bed type not specified"}</span>
                          </div>
                          <div className='flex items-center space-x-[4px]'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <g clipPath="url(#clip0_2846_5970)">
                                <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9.33301 1.82593C9.83492 1.95444 10.2798 2.24634 10.5975 2.65561C10.9151 3.06488 11.0876 3.56825 11.0876 4.08634C11.0876 4.60444 10.9151 5.10781 10.5975 5.51708C10.2798 5.92635 9.83492 6.21825 9.33301 6.34676" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12.25 12.2499V11.0832C12.247 10.5682 12.0738 10.0687 11.7572 9.66247C11.4407 9.25624 10.9986 8.96614 10.5 8.8374" stroke="black" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_2846_5970">
                                  <rect width="14" height="14" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            <span className='text-[12px] text-[#bebebe]'>Sleeps {bookingData.room?.sleeps || "Capacity not specified"}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-[4px]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <g clipPath="url(#clip0_2846_5880)">
                              <path d="M8.16699 7V7.00583" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M1.75 12.25H12.25" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M3.5 12.25V2.91667C3.5 2.60725 3.62292 2.3105 3.84171 2.09171C4.0605 1.87292 4.35725 1.75 4.66667 1.75H9.33333C9.64275 1.75 9.9395 1.87292 10.1583 2.09171C10.3771 2.3105 10.5 2.60725 10.5 2.91667V12.25" stroke="#78736A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                              <clipPath id="clip0_2846_5880">
                                <rect width="14" height="14" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          <span className='text-[12px] leading-none text-[#78736A]'>
                            {getTotalRoomsCount()} Room {getTotalGuestsCount()} guest{getTotalGuestsCount() > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation Policy Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <button
                onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
                className="w-full flex items-center justify-between"
              >
                <span className="font-medium text-[18px] text-black leading-none">Cancellation Policy</span>
                <ChevronDown
                  size={20}
                  className={`text-gray-500 transition-transform ${showCancellationPolicy ? 'rotate-180' : ''
                    }`}
                />
              </button>

              {showCancellationPolicy && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 whitespace-pre-line">
                    {cancellationPolicyText}
                  </p>
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center justify-between w-full">
              <div className="flex items-end gap-[10px]">
              <div className="text-[14px] text-black leading-none ">Total Amount</div>
              <div className="flex items-end gap-[10px]">
                <span className="text-[20px] font-bold text-black leading-none">
                  ₹{stayTotals.total.toLocaleString()}
                </span>
              </div>
              </div>

              {/* Desktop Payment Button - Below Cancellation Policy */}
              <div className="">
              <button
            onClick={handlePayNow}
            disabled={isProcessingPayment}
            className="bg-black w-[210px] text-white text-[16px] h-[56px] leading-[1.1] px-[40px] py-[14px] rounded-[14px] font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessingPayment ? 'Processing...' : 'Pay now'}
          </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Payment Bar - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-[16px] md:hidden">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-[14px] text-black leading-none mb-[4px]">Total Amount</div>
            <div className="flex items-center gap-[8px]">
              <span className="text-[20px] font-bold text-black leading-none">
                ₹{stayTotals.total.toLocaleString()}
              </span>
            </div>
          </div>
          <button
            onClick={handlePayNow}
            disabled={isProcessingPayment}
            className="bg-black text-white text-[16px] h-[56px] leading-[1.1] px-[40px] py-[14px] rounded-[14px] font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessingPayment ? 'Processing...' : 'Pay now'}
          </button>
        </div>
      </div>

      {/* Bottom spacing to prevent content from being hidden behind fixed bar - Mobile Only */}
      <div className="h-24 md:h-0"></div>

      {/* Footer - Desktop Only */}
      <div className="hidden md:block">
        <Footer />
      </div>

      {/* Payment Error Display */}
      {paymentError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex justify-between items-center">
            <span>{paymentError}</span>
            <button
              onClick={() => setPaymentError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Cashfree Payment Component */}
      {paymentSessionId && (
        <CashfreePayment
          paymentSessionId={paymentSessionId}
          orderId={orderId}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          onClose={handlePaymentClose}
        />
      )}

      {/* Debug info in development mode */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 bg-black text-white p-4 text-xs rounded max-w-xs overflow-auto max-h-96">
          <h4 className="font-bold mb-2">Debug Info:</h4>
          <div>Property: {bookingData?.property?.name}</div>
          <div>Selected Rooms: {bookingData?.selectedRooms?.length || 0}</div>
          <div>Total Price: ₹{bookingData?.pricing?.total?.toLocaleString() || "0"}</div>
          <div>User: {bookingData?.user?.name || "N/A"}</div>
          <div className="mt-2">
            <strong>Selected Rooms:</strong>
            {bookingData?.selectedRooms?.map((room, idx) => (
              <div key={idx} className="ml-2">
                {room.quantity}x {room.name} - ₹{room.price}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingReview;
